// Detalle de un ticket del CRM (Fase 4). Muestra los datos del ticket
// (GET /tickets/:id), sus comentarios (GET/POST /tickets/:id/comments) y el
// historial de acciones (GET /tickets/:id/activity). Todos los endpoints existen
// en el BFF del CRM (tickets.plugin.ts). Envuelto en CrmConnectGate.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CrmHttpError, describeCrmError } from "../../api/crm";
import CategoryChipBar, {
  CategoryChipOption,
} from "../../components/crm/CategoryChipBar";
import { useCrmResource } from "../../hooks/useCrmResource";
import {
  addTicketComment,
  deleteTicketComment,
  describeActivity,
  downloadTicketAttachment,
  fetchTicketAttachmentObjectUrl,
  getTicket,
  getTicketActivity,
  listTicketComments,
  loadTicketPipelines,
  PRIORITY_LABELS,
  restoreTicketComment,
  setTicketArchived,
  setTicketCommentPinned,
  updateTicket,
} from "../../api/crmTickets";
import { useAlert, useConfirm } from "../../components/Dialog";
import {
  TicketActivityEntry,
  TicketAttachment,
  TicketComment,
  TicketDetail as TicketDetailData,
  TicketPipeline,
} from "../../api/crmTypes";
import {
  AttachmentPickerUI,
  formatBytes,
  useAttachmentPicker,
} from "../../components/crm/attachmentPicker";
import NoteBody from "../../components/crm/NoteBody";
import NoteEditor, { useNoteComposer } from "../../components/crm/NoteEditor";
import RichNoteBody from "../../components/crm/RichNoteBody";
import { CrmConnectGate, useCrmSession } from "../../context/CrmSessionContext";
import {
  ErrorBox,
  PrimaryButton,
  TextArea,
  TextInput,
} from "../../components/FormControls";
import { IconPin } from "../../components/Icons";
import TicketPipelineSelect from "../../components/crm/TicketPipelineSelect";
import TicketStageSelect from "../../components/crm/TicketStageSelect";
import { fmtDate } from "../../types/promos";
import Pill from "../LeadDetail/components/Pill";
import { Select } from "../../components/FormControls";
import { TypificationSelect } from "../../components/crm/TypificationSelect";
import {
  TICKET_INCIDENT_TYPES,
  ticketPipelineHasIncidentType,
  typificationsForPipeline,
} from "../../api/crmTicketTypification";

export default function TicketDetail() {
  const { ticketId } = useParams<{ ticketId: string }>();
  return (
    <div style={{ padding: "1.5rem", maxWidth: 900 }}>
      <Link to="/operations/tickets" style={{ color: "#0069d9", fontSize: ".85rem" }}>
        ← Volver a Tickets
      </Link>
      <CrmConnectGate>
        {ticketId ? (
          <TicketDetailInner ticketId={ticketId} />
        ) : (
          <div style={{ marginTop: "1rem", color: "#b32424" }}>Ticket no válido.</div>
        )}
      </CrmConnectGate>
    </div>
  );
}

/** Edición de subject/description (PATCH /tickets/:id, roles admin|ops). */
function EditTicketForm({
  t,
  onSaved,
  onCancel,
}: {
  t: TicketDetailData;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const alert = useAlert();
  const [subject, setSubject] = useState(t.subject);
  const [description, setDescription] = useState(t.description ?? "");
  const [incidentType, setIncidentType] = useState(t.incidentType ?? "");
  const [typification, setTypification] = useState(t.typification ?? "");
  const [saving, setSaving] = useState(false);

  // Misma lógica de escopado que el alta: Opción 1 si el pipeline tipifica,
  // Opción 2 escopada al pipeline del ticket (fijo en la ficha).
  const showIncidentType = ticketPipelineHasIncidentType(t.pipelineId);
  const typificationGroups = typificationsForPipeline(t.pipelineId);

  const save = async () => {
    const s = subject.trim();
    if (!s) return;
    setSaving(true);
    try {
      // Enviamos SIEMPRE la tipificación (null = quitar): el PATCH acepta los
      // campos nullable y el backend valida la Opción 2 contra el pipeline.
      await updateTicket(t.id, {
        subject: s,
        description: description.trim() === "" ? null : description,
        incidentType: incidentType || null,
        typification: typification || null,
      });
      onSaved();
    } catch (err) {
      await alert({
        title: "No se pudo guardar",
        message:
          err instanceof CrmHttpError
            ? err.status === 403
              ? "No tienes permisos para editar tickets (requiere rol ops o admin en el CRM)."
              : describeCrmError(err)
            : "Error de conexión con el CRM.",
        tone: "danger",
      });
      setSaving(false);
    }
  };

  return (
    <div style={{ ...card, marginTop: "0.8rem" }}>
      <label style={{ display: "block", fontSize: ".72rem", color: "#73879C", marginBottom: "0.25rem" }}>
        NOMBRE DEL TICKET
      </label>
      <TextInput
        value={subject}
        onChange={setSubject}
        maxLength={500}
        placeholder="Asunto del ticket"
      />
      <label style={{ display: "block", fontSize: ".72rem", color: "#73879C", margin: "0.7rem 0 0.25rem" }}>
        DESCRIPCIÓN
      </label>
      <TextArea
        value={description}
        onChange={setDescription}
        maxLength={10000}
        rows={4}
        placeholder="Descripción (opcional)"
      />
      {showIncidentType && (
        <>
          <label style={{ display: "block", fontSize: ".72rem", color: "#73879C", margin: "0.7rem 0 0.25rem" }}>
            TIPO DE INCIDENCIA
          </label>
          <Select
            value={incidentType}
            onChange={setIncidentType}
            options={TICKET_INCIDENT_TYPES.map((it) => ({ value: it, label: it }))}
            placeholder="—"
          />
        </>
      )}
      {typificationGroups.length > 0 && (
        <>
          <label style={{ display: "block", fontSize: ".72rem", color: "#73879C", margin: "0.7rem 0 0.25rem" }}>
            TIPIFICACIÓN
          </label>
          <TypificationSelect
            value={typification}
            onChange={setTypification}
            groups={typificationGroups}
          />
        </>
      )}
      <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.7rem" }}>
        <PrimaryButton onClick={() => void save()} disabled={saving || !subject.trim()}>
          {saving ? "Guardando…" : "Guardar"}
        </PrimaryButton>
        <button type="button" onClick={onCancel} disabled={saving} style={editBtn}>
          Cancelar
        </button>
      </div>
    </div>
  );
}

/** El CRM no borra tickets: se ARCHIVAN (desaparecen de listados y tablero;
 *  queda rastro en el Historial). Mismo modelo que el CRM; roles admin|ops. */
function ArchiveButton({
  ticketId,
  archived,
}: {
  ticketId: string;
  archived: boolean;
}) {
  const confirm = useConfirm();
  const alert = useAlert();
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);

  const onClick = async () => {
    const ok = await confirm({
      title: archived ? "Restaurar ticket" : "Archivar ticket",
      message: archived
        ? "El ticket volverá a aparecer en los listados y el tablero."
        : "Archivar NO es cerrar: si lo que quieres es dar el ticket por resuelto, cámbiale el stage a uno de «Cerrados» en lugar de archivarlo.\n\nArchivar hace desaparecer el ticket de los listados y del tablero (también en el CRM). Quedará rastro en el Historial y se puede restaurar.",
      tone: archived ? "primary" : "danger",
      confirmLabel: archived ? "Restaurar" : "Archivar",
    });
    if (!ok) return;
    setPending(true);
    try {
      await setTicketArchived(ticketId, !archived);
      if (archived) {
        window.location.reload();
      } else {
        navigate("/operations/tickets");
      }
    } catch (err) {
      await alert({
        title: "No se pudo completar",
        message:
          err instanceof CrmHttpError
            ? err.status === 403
              ? "No tienes permisos para archivar tickets (requiere rol ops o admin en el CRM)."
              : describeCrmError(err)
            : "Error de conexión con el CRM.",
        tone: "danger",
      });
      setPending(false);
    }
  };

  return (
    <button
      type="button"
      onClick={() => void onClick()}
      disabled={pending}
      style={{
        borderRadius: "0.4rem",
        border: "1px solid #f5c6cb",
        background: "white",
        color: "#b32424",
        padding: "0.3rem 0.7rem",
        fontSize: ".82rem",
        cursor: pending ? "default" : "pointer",
        opacity: pending ? 0.6 : 1,
      }}
    >
      {pending ? "…" : archived ? "Restaurar" : "Archivar"}
    </button>
  );
}

function TicketDetailInner({ ticketId }: { ticketId: string }) {
  // Los tres recursos se montan a la vez (mismo nivel), así comments/activity
  // empiezan a cargar en paralelo con el ticket en lugar de esperar a que este
  // resuelva. El guard de abajo solo condiciona el RENDER del bloque principal.
  const ticket = useCrmResource<TicketDetailData>(
    () => getTicket(ticketId),
    [ticketId],
    "No se pudo cargar el ticket",
  );

  return (
    <div style={{ marginTop: "1rem" }}>
      <TicketMain ticket={ticket} />
      <Comments ticketId={ticketId} />
      <Activity ticketId={ticketId} />
    </div>
  );
}

// Bloque principal del ticket: cabecera + ficha. Es lo único que depende de que
// el fetch del ticket haya resuelto; comments/activity se renderizan aparte.
function TicketMain({
  ticket,
}: {
  ticket: ReturnType<typeof useCrmResource<TicketDetailData>>;
}) {
  const [editing, setEditing] = useState(false);
  if (ticket.loading && ticket.data === null) {
    return <Muted>Cargando ticket…</Muted>;
  }
  if (ticket.error) {
    // El 404 no es un fallo transitorio: no ofrecemos "Reintentar".
    if (ticket.errorStatus === 404) {
      return (
        <div style={{ marginTop: "1rem" }}>
          <Muted>Ticket no encontrado.</Muted>
          <div style={{ marginTop: "0.5rem" }}>
            <Link to="/operations/tickets" style={{ color: "#0069d9", fontSize: ".85rem" }}>
              Volver a Tickets
            </Link>
          </div>
        </div>
      );
    }
    return (
      <div>
        <ErrorBox>{ticket.error}</ErrorBox>
        <button type="button" onClick={ticket.reload} style={retryBtn}>
          Reintentar
        </button>
      </div>
    );
  }
  if (!ticket.data) return null;

  const t = ticket.data;
  const closed = t.stageState === "closed";

  return (
    <>
      <div style={{ display: "flex", alignItems: "baseline", gap: "0.7rem", flexWrap: "wrap" }}>
        <h1 style={{ margin: 0, fontSize: "1.2rem", color: "#2A3F54" }}>{t.subject}</h1>
        <Pill label={closed ? "Cerrado" : "Abierto"} scheme={closed ? "muted" : "green"} />
        {t.archived && <Pill label="Archivado" scheme="amber" />}
        <span style={{ marginLeft: "auto", display: "inline-flex", gap: "0.4rem" }}>
          {!editing && (
            <button type="button" onClick={() => setEditing(true)} style={editBtn}>
              Editar
            </button>
          )}
          <ArchiveButton ticketId={t.id} archived={t.archived === true} />
        </span>
      </div>

      {editing && (
        <EditTicketForm
          t={t}
          onSaved={() => {
            setEditing(false);
            ticket.reload();
          }}
          onCancel={() => setEditing(false)}
        />
      )}

      <div style={card}>
        <Row label="Pipeline">
          <div style={{ maxWidth: 320 }}>
            <TicketPipelineSelect
              ticketId={t.id}
              pipelineId={t.pipelineId}
              pipelineLabel={t.pipelineLabel}
              onMoved={ticket.reload}
            />
            <div style={{ fontSize: ".7rem", color: "#73879C", marginTop: "0.25rem" }}>
              Al cambiar de pipeline, el ticket aterriza en el primer stage
              abierto del destino.
            </div>
          </div>
        </Row>
        <Row label="Stage">
          <div style={{ maxWidth: 320 }}>
            <TicketStageSelect
              ticketId={t.id}
              pipelineId={t.pipelineId}
              stageId={t.stageId}
              stageLabel={t.stageLabel}
              onMoved={ticket.reload}
            />
            <div style={{ fontSize: ".7rem", color: "#73879C", marginTop: "0.25rem" }}>
              {closed
                ? "El ticket está cerrado. Muévelo a un stage abierto para reabrirlo."
                : "Para cerrar el ticket, muévelo a un stage de «Cerrados». Archivar no lo cierra."}
            </div>
          </div>
        </Row>
        <Row label="Tipo incidencia">
          {t.incidentType ?? <span style={{ color: "#adb5bd" }}>—</span>}
        </Row>
        <Row label="Tipificación">
          {t.typification ?? <span style={{ color: "#adb5bd" }}>—</span>}
        </Row>
        <Row label="Asignado">
          {t.assignedTo ?? <span style={{ color: "#adb5bd" }}>Sin asignar</span>}
        </Row>
        <Row label="Prioridad">
          {t.priority ? PRIORITY_LABELS[t.priority] : <span style={{ color: "#adb5bd" }}>—</span>}
        </Row>
        <Row label="Mercado">{t.market ?? <span style={{ color: "#adb5bd" }}>—</span>}</Row>
        <Row label="Origen">{t.source}</Row>
        <Row label="Lead">
          {t.leadId ? (
            <Link to={`/leads/${t.leadId}`} style={{ color: "#0069d9", fontWeight: 600 }}>
              {t.lead?.fullName ?? t.lead?.email ?? "Ver lead"}
            </Link>
          ) : (
            <span style={{ color: "#adb5bd" }}>Sin lead asociado</span>
          )}
        </Row>
        <Row label="Creado">{fmtDate(t.createdAt)}</Row>
        <Row label="Últ. actividad">{fmtDate(t.lastActivityAt)}</Row>
        {t.closedAt && <Row label="Cerrado">{fmtDate(t.closedAt)}</Row>}
        {t.description && (
          <Row label="Descripción">
            <div style={{ whiteSpace: "pre-wrap" }}>{t.description}</div>
          </Row>
        )}
      </div>
    </>
  );
}

function Comments({ ticketId }: { ticketId: string }) {
  const comments = useCrmResource<TicketComment[]>(
    () => listTicketComments(ticketId),
    [ticketId],
    "No se pudieron cargar los comentarios",
  );

  const [submitting, setSubmitting] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);
  const picker = useAttachmentPicker();
  // Composer con formato opcional (mismo modelo que el CRM): textarea sencillo
  // por defecto; «Aa · Texto con formato» activa el editor rico para esa nota.
  const note = useNoteComposer(picker);

  // Estable por ticket (contrato de RichNoteBody: si cambiara por render,
  // bucle de descargas de imágenes).
  const fetchObjectUrl = useCallback(
    (attachmentId: string) =>
      fetchTicketAttachmentObjectUrl(ticketId, attachmentId),
    [ticketId],
  );

  const submit = useCallback(async () => {
    if (submitting) return;
    const payload = note.toPayload();
    if (!payload) return;
    // La nota es publicable si tiene texto/HTML O al menos un adjunto (el CRM
    // acepta notas solo-adjunto; el contrato exige texto o >=1 adjunto).
    if (
      !payload.body &&
      !payload.bodyHtml &&
      (payload.attachments?.length ?? 0) === 0
    ) {
      // Solo alcanzable por el atajo Ctrl+Enter con la nota efectivamente
      // vacía (el botón ya se deshabilita con hasContent): feedback en vez de
      // un no-op silencioso.
      setPostError("La nota está vacía.");
      return;
    }
    setSubmitting(true);
    setPostError(null);
    try {
      await addTicketComment(ticketId, payload);
      note.reset();
      comments.reload();
    } catch (err) {
      setPostError(
        err instanceof CrmHttpError
          ? describeCrmError(err)
          : "No se pudo publicar el comentario",
      );
    } finally {
      setSubmitting(false);
    }
  }, [submitting, note, ticketId, comments]);

  // Identidad del CRM para el gating por autor/rol (el server manda: aquí solo
  // se decide si se pintan los botones). Las notas BORRADAS solo llegan a los
  // admins (el CRM las filtra para el resto), así que se muestran tal cual:
  // atenuadas, con quién las borró y el botón de restaurar (mismo UI que el CRM).
  const { me } = useCrmSession();
  const isAdmin = me?.role === "admin";
  const items = comments.data ?? [];

  return (
    <section style={{ marginTop: "1.5rem" }}>
      <h2 style={sectionTitle}>Comentarios</h2>

      <div style={{ marginBottom: "0.8rem" }}>
        <NoteEditor
          state={note}
          picker={picker}
          rows={3}
          placeholder="Escribe un comentario o pega una captura… (Ctrl+Enter para publicar)"
          disabled={submitting}
          onSubmitShortcut={() => void submit()}
        />
        <AttachmentPickerUI picker={picker} disabled={submitting} />
        {postError && <ErrorBox>{postError}</ErrorBox>}
        <div style={{ marginTop: "0.5rem" }}>
          <PrimaryButton onClick={submit} disabled={submitting || !note.hasContent}>
            {submitting ? "Publicando…" : "Publicar"}
          </PrimaryButton>
        </div>
      </div>

      {comments.loading && comments.data === null && <Muted>Cargando comentarios…</Muted>}
      {comments.error && (
        <div>
          <ErrorBox>{comments.error}</ErrorBox>
          <button type="button" onClick={comments.reload} style={retryBtn}>
            Reintentar
          </button>
        </div>
      )}
      {comments.data !== null && items.length === 0 && !comments.error && (
        <Muted>Sin comentarios.</Muted>
      )}

      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {items.map((c) => (
          <li
            key={c.id}
            style={{
              ...commentItem,
              // Nota fijada: mismo resaltado ámbar que el CRM. El listado ya
              // llega con las fijadas ARRIBA (orden del servidor).
              ...(c.pinnedAt && !c.deletedAt
                ? { background: "#fffbeb", border: "1px solid #fcd34d" }
                : null),
              ...(c.deletedAt ? { opacity: 0.55 } : null),
            }}
          >
            {c.deletedAt && (
              <div style={{ marginBottom: "0.25rem", fontSize: ".72rem", color: "#b91c1c" }}>
                Borrada por {c.deletedBy ?? "—"} · {fmtDate(c.deletedAt)}
                {isAdmin && (
                  <RestoreCommentButton
                    ticketId={ticketId}
                    commentId={c.id}
                    onChanged={comments.reload}
                  />
                )}
              </div>
            )}
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "baseline" }}>
              <strong style={{ fontSize: ".82rem", color: "#1f2933" }}>
                {c.actorName ?? c.actor}
              </strong>
              <span style={{ fontSize: ".72rem", color: "#73879C" }}>
                {fmtDate(c.createdAt)}
              </span>
              <span
                style={{
                  marginLeft: "auto",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                {/* Espejo del gate REAL del CRM: la ruta DELETE exige rol
                    admin|ops (requireRole) Y el service exige autor-o-admin —
                    un autor con rol sales recibiría 403, así que no se le
                    pinta el botón. */}
                {!c.deletedAt &&
                  (isAdmin ||
                    (me?.role === "ops" && c.actor === me.email)) && (
                    <DeleteCommentButton
                      ticketId={ticketId}
                      commentId={c.id}
                      onChanged={comments.reload}
                    />
                  )}
                {!c.deletedAt && (
                  <PinButton ticketId={ticketId} comment={c} onChanged={comments.reload} />
                )}
              </span>
            </div>
            {c.bodyHtml ? (
              <RichNoteBody
                html={c.bodyHtml}
                attachments={c.attachments ?? []}
                fetchObjectUrl={fetchObjectUrl}
              />
            ) : c.body ? (
              <NoteBody
                body={c.body}
                attachments={c.attachments ?? []}
                fetchObjectUrl={fetchObjectUrl}
              />
            ) : null}
            {(c.attachments?.length ?? 0) > 0 && (
              <ul
                style={{
                  listStyle: "none",
                  margin: "0.4rem 0 0",
                  padding: 0,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "0.4rem",
                }}
              >
                {c.attachments!.map((a) => (
                  <li key={a.id}>
                    <AttachmentDownload
                      ticketId={ticketId}
                      attachmentId={a.id}
                      filename={a.filename}
                      sizeBytes={a.sizeBytes}
                    />
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

// BORRADO LÓGICO de una nota (solo su autor o un admin — mismo espejo del guard
// del CRM que decide pintar el botón; el server revalida y da 403). La fila se
// conserva con deletedAt/deletedBy y rastro note_deleted en el Historial; los
// admins la siguen viendo marcada y pueden restaurarla.
function DeleteCommentButton({
  ticketId,
  commentId,
  onChanged,
}: {
  ticketId: string;
  commentId: string;
  onChanged: () => void;
}) {
  const confirm = useConfirm();
  const alert = useAlert();
  const [pending, setPending] = useState(false);

  const onClick = async () => {
    if (pending) return;
    const ok = await confirm({
      title: "Borrar nota",
      message:
        "Borrar NO elimina la nota: queda registrada (quién y cuándo) y un admin puede restaurarla. Dejará de verse en el listado del equipo.",
      tone: "danger",
      confirmLabel: "Borrar",
    });
    if (!ok) return;
    setPending(true);
    try {
      await deleteTicketComment(ticketId, commentId);
      onChanged();
    } catch (err) {
      await alert({
        title: "No se pudo borrar",
        message:
          err instanceof CrmHttpError
            ? err.status === 403
              ? "Solo el autor de la nota o un admin pueden borrarla."
              : describeCrmError(err)
            : "Error de conexión con el CRM.",
        tone: "danger",
      });
    } finally {
      setPending(false);
    }
  };

  return (
    <button
      type="button"
      onClick={() => void onClick()}
      disabled={pending}
      style={{
        border: "none",
        background: "transparent",
        color: "#b32424",
        padding: 0,
        fontSize: ".72rem",
        cursor: pending ? "default" : "pointer",
        textDecoration: "underline",
        opacity: pending ? 0.5 : 1,
      }}
    >
      {pending ? "Borrando…" : "Borrar"}
    </button>
  );
}

// Restaurar una nota borrada (solo admin en el CRM; deja rastro note_restored).
function RestoreCommentButton({
  ticketId,
  commentId,
  onChanged,
}: {
  ticketId: string;
  commentId: string;
  onChanged: () => void;
}) {
  const alert = useAlert();
  const [pending, setPending] = useState(false);

  const onClick = async () => {
    if (pending) return;
    setPending(true);
    try {
      await restoreTicketComment(ticketId, commentId);
      onChanged();
    } catch (err) {
      await alert({
        title: "No se pudo restaurar",
        message:
          err instanceof CrmHttpError
            ? err.status === 403
              ? "Solo un admin puede restaurar una nota borrada."
              : describeCrmError(err)
            : "Error de conexión con el CRM.",
        tone: "danger",
      });
    } finally {
      setPending(false);
    }
  };

  return (
    <button
      type="button"
      onClick={() => void onClick()}
      disabled={pending}
      style={{
        marginLeft: "0.5rem",
        border: "none",
        background: "transparent",
        color: "#0069d9",
        padding: 0,
        fontSize: ".72rem",
        cursor: pending ? "default" : "pointer",
        textDecoration: "underline",
        opacity: pending ? 0.5 : 1,
      }}
    >
      {pending ? "Restaurando…" : "Restaurar"}
    </button>
  );
}

// Chincheta de una nota del ticket. Es la MISMA columna que el pin del timeline
// del lead en el CRM: fijar aquí también la fija en la ficha del lead asociado,
// y viceversa. Tras el toggle se recarga el listado (el servidor devuelve las
// fijadas arriba). Roles admin|ops|sales — fijar es acción de equipo.
function PinButton({
  ticketId,
  comment,
  onChanged,
}: {
  ticketId: string;
  comment: TicketComment;
  onChanged: () => void;
}) {
  const alert = useAlert();
  const [pending, setPending] = useState(false);
  const pinned = comment.pinnedAt != null;

  const onClick = async () => {
    if (pending) return;
    setPending(true);
    try {
      await setTicketCommentPinned(ticketId, comment.id, !pinned);
      onChanged();
    } catch (err) {
      await alert({
        title: "No se pudo completar",
        message:
          err instanceof CrmHttpError
            ? describeCrmError(err)
            : "Error de conexión con el CRM.",
        tone: "danger",
      });
    } finally {
      setPending(false);
    }
  };

  return (
    <button
      type="button"
      onClick={() => void onClick()}
      disabled={pending}
      title={
        pinned
          ? "Quitar de fijadas (también en la ficha del lead del CRM)"
          : "Fijar arriba (también en la ficha del lead del CRM)"
      }
      aria-label={pinned ? "Desfijar nota" : "Fijar nota"}
      aria-pressed={pinned}
      style={{
        border: "none",
        background: "transparent",
        color: pinned ? "#d97706" : "#ced4da",
        padding: "0.1rem",
        cursor: pending ? "default" : "pointer",
        opacity: pending ? 0.5 : 1,
        display: "inline-flex",
      }}
    >
      <IconPin style={{ width: "0.9rem", height: "0.9rem" }} />
    </button>
  );
}

// Botón de descarga de un adjunto de nota. La descarga va SIEMPRE por la API
// (stream autenticado por cookie, bucket privado): crmDownload trae el blob y
// dispara la descarga con el nombre real. Estado propio para el "Descargando…"
// y el error inline por adjunto (403 con rol insuficiente → describeCrmError).
function AttachmentDownload({
  ticketId,
  attachmentId,
  filename,
  sizeBytes,
}: {
  ticketId: string;
  attachmentId: string;
  filename: string;
  sizeBytes: number;
}) {
  const [downloading, setDownloading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onClick = useCallback(async () => {
    if (downloading) return;
    setDownloading(true);
    setErr(null);
    try {
      await downloadTicketAttachment(ticketId, attachmentId, filename);
    } catch (e) {
      setErr(
        e instanceof CrmHttpError
          ? describeCrmError(e)
          : "No se pudo descargar el adjunto",
      );
    } finally {
      setDownloading(false);
    }
  }, [downloading, ticketId, attachmentId, filename]);

  return (
    <div>
      <button
        type="button"
        onClick={onClick}
        disabled={downloading}
        style={chip}
        title={`Descargar ${filename} (${formatBytes(sizeBytes)})`}
      >
        <span
          style={{
            maxWidth: 200,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          📎 {filename}
        </span>
        <span style={{ color: "#73879C" }}>
          {downloading ? "Descargando…" : formatBytes(sizeBytes)}
        </span>
      </button>
      {err && (
        <div style={{ fontSize: ".7rem", color: "#b32424", marginTop: "0.15rem" }}>
          {err}
        </div>
      )}
    </div>
  );
}

// Filtro del historial. A diferencia del timeline del lead (que clasifica por
// WhatsApp/Emails/Notas), el log de auditoría del ticket solo tiene los tres
// tipos de TicketActivityEntry, así que los chips son esos.
type ActivityCategory = "todos" | "etapa" | "asignacion" | "acciones";

const ACTIVITY_CATEGORY_OPTIONS: CategoryChipOption<ActivityCategory>[] = [
  { key: "todos", label: "Todos" },
  { key: "etapa", label: "Etapa" },
  { key: "asignacion", label: "Asignación" },
  { key: "acciones", label: "Acciones" },
];

function activityCategoryOf(
  e: TicketActivityEntry,
): Exclude<ActivityCategory, "todos"> {
  if (e.type === "stage") return "etapa";
  if (e.type === "assignment") return "asignacion";
  // 'action' agrupa el resto (pipeline_moved, note_deleted, edited…).
  return "acciones";
}

function Activity({ ticketId }: { ticketId: string }) {
  const activity = useCrmResource<TicketActivityEntry[]>(
    () => getTicketActivity(ticketId),
    [ticketId],
    "No se pudo cargar el historial",
  );

  // Las entradas 'pipeline_moved' guardan IDs de pipeline en `detail`; con el
  // mapa id → label pintamos nombres. Comparte la promesa cacheada con el
  // TicketPipelineSelect de la ficha (loadTicketPipelines), así que no añade
  // peticiones. Si falla, describeActivity degrada a pintar los IDs.
  const pipelines = useCrmResource<TicketPipeline[]>(
    () => loadTicketPipelines(),
    [],
    "No se pudieron cargar los pipelines",
  );
  const pipelineLabels = useMemo(
    () =>
      Object.fromEntries((pipelines.data ?? []).map((p) => [p.id, p.label])),
    [pipelines.data],
  );

  const [category, setCategory] = useState<ActivityCategory>("todos");
  const loaded = useMemo(() => activity.data ?? [], [activity.data]);
  const list = useMemo(
    () =>
      category === "todos"
        ? loaded
        : loaded.filter((e) => activityCategoryOf(e) === category),
    [loaded, category],
  );

  return (
    <section style={{ marginTop: "1.5rem" }}>
      <h2 style={sectionTitle}>Historial</h2>
      {activity.data !== null && loaded.length > 0 && (
        <CategoryChipBar
          options={ACTIVITY_CATEGORY_OPTIONS}
          value={category}
          onChange={setCategory}
          shown={list.length}
          total={loaded.length}
          allKey="todos"
          ariaLabel="Filtrar historial"
        />
      )}
      {activity.loading && activity.data === null && <Muted>Cargando historial…</Muted>}
      {activity.error && (
        <div>
          <ErrorBox>{activity.error}</ErrorBox>
          <button type="button" onClick={activity.reload} style={retryBtn}>
            Reintentar
          </button>
        </div>
      )}
      {activity.data !== null && list.length === 0 && !activity.error && (
        <Muted>
          {loaded.length === 0
            ? "Sin actividad registrada."
            : "Sin actividad de este tipo."}
        </Muted>
      )}
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {list.map((e, i) => (
          // La key deriva del contenido (type + timestamp) con `i` solo como
          // desempate final para entradas idénticas en el mismo instante.
          <li key={`${e.type}:${e.occurredAt}:${i}`} style={activityItem}>
            <span style={{ fontSize: ".72rem", color: "#73879C", minWidth: 130 }}>
              {fmtDate(e.occurredAt)}
            </span>
            <span style={{ fontSize: ".83rem" }}>
              {describeActivity(e, pipelineLabels)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", gap: "0.8rem", padding: "0.35rem 0", fontSize: ".85rem" }}>
      <div
        style={{
          minWidth: 130,
          color: "#73879C",
          fontWeight: 600,
          fontSize: ".72rem",
          textTransform: "uppercase",
          letterSpacing: ".3px",
          paddingTop: "0.1rem",
        }}
      >
        {label}
      </div>
      <div style={{ flex: 1, color: "#1f2933" }}>{children}</div>
    </div>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <div style={{ color: "#73879C", fontSize: ".82rem" }}>{children}</div>;
}

const card: React.CSSProperties = {
  background: "white",
  borderRadius: "0.55rem",
  boxShadow: "0 1px 3px rgba(0,0,0,.04)",
  padding: "1rem 1.2rem",
  marginTop: "0.9rem",
};

const sectionTitle: React.CSSProperties = {
  margin: "0 0 0.7rem",
  fontSize: ".95rem",
  color: "#2A3F54",
};

const commentItem: React.CSSProperties = {
  border: "1px solid #e9ecef",
  borderRadius: "0.5rem",
  padding: "0.55rem 0.7rem",
  marginBottom: "0.5rem",
  background: "white",
};

const chip: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.4rem",
  border: "1px solid #dee2e6",
  borderRadius: "0.4rem",
  background: "white",
  color: "#1f2933",
  padding: "0.25rem 0.5rem",
  fontSize: ".76rem",
  cursor: "pointer",
};

const activityItem: React.CSSProperties = {
  display: "flex",
  gap: "0.7rem",
  alignItems: "baseline",
  padding: "0.35rem 0",
  borderTop: "1px solid #f1f3f5",
};

const editBtn: React.CSSProperties = {
  borderRadius: "0.4rem",
  border: "1px solid #bfdbfe",
  background: "white",
  color: "#0069d9",
  padding: "0.3rem 0.7rem",
  fontSize: ".82rem",
  cursor: "pointer",
};

const retryBtn: React.CSSProperties = {
  marginTop: "0.5rem",
  padding: "0.35rem 0.75rem",
  borderRadius: "0.4rem",
  background: "white",
  color: "#0069d9",
  border: "1px solid #dee2e6",
  fontSize: ".8rem",
  fontWeight: 600,
  cursor: "pointer",
};
